/**
 * FloorplanRepresentationManager — resolves the wall representation PER FLOOR
 * from the floor's `floor_wall_source` property (the same state the Floor
 * panel's "Source" select shows):
 *
 * - 'WALL_SKETCH' → the skinned wall visualization (2D fill / 3D extruded
 *   walls with openings stamped out).
 * - 'ROOMS' ("Measured") → the measured view: NO wall fill — the skin's
 *   boundary outline (openings still cut). The skeleton graph is hidden
 *   entirely in this representation (walls come from the rooms/outline, so
 *   skeleton errors don't matter; the door/window tools materialize sketch
 *   edges on demand from the room/outline edges).
 *
 * The source is a GLOBAL switch: it applies in every tab and persona (the
 * old rule scoped it to the floorplan persona's Wall/Design tabs). Tab state
 * still refines SECONDARY behavior — the Design tab is the clean result view,
 * and the Floor/Room tabs keep the room/outline lines visible for editing.
 *
 * Tools that need representation-dependent behavior branch on
 * `viewer.floorplanRepresentation.isOriginalActive(floorGroupId)`.
 */

import { getFloorWallSourceProperty, hasGlobalPropertyService } from '@pointorama/potree-shared';
import type { Viewer } from './viewer';
import type { Scene } from './Scene';

/** Scene events that reshape the measured view's error classification. */
const SCENE_ANNOTATION_EVENTS = ['room_added', 'room_removed', 'outline_added', 'outline_removed'] as const;

export type FloorplanRepresentationMode = 'ORIGINAL' | 'SKINNED';

export class FloorplanRepresentationManager {
  private viewer: Viewer;
  private listeners = new Set<() => void>();
  private personaListener: () => void;
  private modeListener: () => void;
  /** Active persona whose `mode_changed` we're subscribed to (tab scoping). */
  private attachedPersona: { addEventListener(type: string, cb: () => void): void; removeEventListener(type: string, cb: () => void): void } | null = null;
  private annotationListener: () => void;
  private sceneChangedListener: (event: { type: string; scene?: Scene }) => void;
  private attachedScene: Scene | null = null;
  /** The floorplan persona's current mode id (null outside the persona). */
  private currentModeId: string | null = null;
  /** Active "show rooms/outlines despite SKINNED" gestures (wall-thickness drags). */
  private originalPeekCount = 0;

  constructor(viewer: Viewer) {
    this.viewer = viewer;
    this.modeListener = () => this.refresh();
    this.personaListener = () => {
      this.attachActivePersona();
      this.refresh();
    };
    viewer.personaManager?.addEventListener('persona_manager_stack_changed', this.personaListener);
    this.attachActivePersona();

    // Rooms/outlines appearing or disappearing reshape the measured view —
    // rebuild when they do. (Polygon EDITS are covered by the wall sketch's
    // own property subscriptions.) The source is global, so this applies
    // regardless of tab/persona.
    this.annotationListener = () => {
      this.rebuildWallSketches();
    };
    this.sceneChangedListener = (event) => {
      this.attachScene(event.scene ?? null);
      this.rebuildWallSketches();
    };
    this.attachScene(viewer.scene ?? null);
    viewer.addEventListener('scene_changed', this.sceneChangedListener);
    this.currentModeId = this.computeModeId();
  }

  /** Follow the active persona's tab switches — the representation split only
   *  applies in the Wall and Design tabs. */
  private attachActivePersona(): void {
    this.attachedPersona?.removeEventListener('mode_changed', this.modeListener);
    this.attachedPersona = this.viewer.personaManager?.getActivePersona() ?? null;
    this.attachedPersona?.addEventListener('mode_changed', this.modeListener);
  }

  /** Move the room/outline add-remove listeners to the (new) scene. */
  private attachScene(scene: Scene | null): void {
    if (this.attachedScene) {
      for (const type of SCENE_ANNOTATION_EVENTS) this.attachedScene.removeEventListener(type, this.annotationListener);
    }
    this.attachedScene = scene;
    if (scene) {
      for (const type of SCENE_ANNOTATION_EVENTS) scene.addEventListener(type, this.annotationListener);
    }
  }

  private computeModeId(): string | null {
    const persona = this.viewer.personaManager?.getActivePersona();
    return persona?.name === 'floorplan' ? (persona.getCurrentMode()?.id ?? null) : null;
  }

  /** True while the floorplan persona's DESIGN tab is active — the clean
   *  result view: the skeleton graph and the room/outline measure lines are
   *  hidden there regardless of the representation. */
  isDesignActive(): boolean {
    return this.currentModeId === 'design';
  }

  /**
   * The floor's stored representation preference — the Floor panel's "Source"
   * select ('WALL_SKETCH' ↔ Wall sketch, 'ROOMS' ↔ Measured).
   */
  getModeForFloor(floorGroupId: string | null | undefined): FloorplanRepresentationMode {
    if (!floorGroupId || !hasGlobalPropertyService()) return 'SKINNED';
    try {
      return getFloorWallSourceProperty(floorGroupId).value === 'ROOMS' ? 'ORIGINAL' : 'SKINNED';
    } catch {
      return 'SKINNED';
    }
  }

  /** Write the floor's representation (persists + syncs via the property pipeline). */
  setModeForFloor(floorGroupId: string, mode: FloorplanRepresentationMode): void {
    if (!hasGlobalPropertyService()) return;
    try {
      const property = getFloorWallSourceProperty(floorGroupId);
      const value = mode === 'ORIGINAL' ? 'ROOMS' : 'WALL_SKETCH';
      if (property.value !== value) property.value = value;
    } catch {
      /* property service not ready */
    }
  }

  /**
   * The effective state for one floor — the extension point for
   * representation-dependent behavior. GLOBAL: the floor's source alone
   * decides; no tab/persona gate (the old Wall/Design-tabs-only rule is
   * retired — walls render per their source everywhere).
   */
  isOriginalActive(floorGroupId: string | null | undefined): boolean {
    return this.getModeForFloor(floorGroupId) === 'ORIGINAL';
  }

  /**
   * Transient "peek" at the room/outline lines while a wall-thickness drag is
   * active — the SKINNED representation hides them otherwise, but resizing a
   * wall needs the underlying lines visible for reference. Counter-based so
   * overlapping gestures can't cancel each other's peek.
   */
  beginOriginalPeek(): void {
    this.originalPeekCount++;
  }

  endOriginalPeek(): void {
    this.originalPeekCount = Math.max(0, this.originalPeekCount - 1);
  }

  /**
   * True when the floor's room + outline annotation lines should be hidden:
   * ONLY in the DESIGN tab (the clean result view). Every other tab keeps
   * them visible — the Wall tab regardless of the floor's Source (the lines
   * are the reference geometry wall editing works against), the Floor/Room
   * tabs because they edit rooms/outlines, and other personas because the
   * skin doesn't render there, so the lines are the only wall depiction.
   * A thickness-drag peek also un-hides. Evaluated per frame by the render
   * blacklist choke point and by picking.
   */
  shouldHideOriginalAnnotations(_floorGroupId: string | null | undefined): boolean {
    if (!this.mayHideOriginalAnnotations()) return false;
    // ONLY the Design tab hides a floor's rooms/outlines (the clean result
    // view). The Wall tab keeps them visible regardless of the floor's
    // Source — they are the reference geometry wall editing works against.
    return this.currentModeId === 'design';
  }

  /**
   * Cheap floor-independent precondition for {@link shouldHideOriginalAnnotations}
   * — the render blacklist's per-frame fast path: false in contexts where no
   * floor's lines can be force-hidden.
   */
  mayHideOriginalAnnotations(): boolean {
    if (this.originalPeekCount > 0) return false;
    const persona = this.viewer.personaManager?.getActivePersona()?.name;
    // The wall skin only renders in the floorplan/layout personas (see
    // AnnotationBlacklistStack's hideSketches gate) — elsewhere the lines
    // must stay.
    if (persona !== 'floorplan' && persona !== 'layout') return false;
    // Floor/Room tabs edit rooms & outlines — the lines stay for editing.
    // (Persona mode ids: the Floor tab is 'floor_select', the Room tab is
    // 'floorplan' — NOT the UI labels 'floor'/'room'.)
    if (persona === 'floorplan' && (this.currentModeId === 'floor_select' || this.currentModeId === 'floorplan')) return false;
    return true;
  }

  /** Subscribe to persona/tab state changes. Returns an unsubscribe function. */
  subscribe(callback: () => void): () => void {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Recompute the tab state; on change, rebuild wall-sketch visuals (they
   * read `isOriginalActive()` at build time) and notify subscribers.
   * Per-floor SOURCE changes don't come through here — each wall sketch
   * subscribes to its own floor's property and rebuilds itself.
   */
  private refresh(): void {
    const modeId = this.computeModeId();
    if (modeId === this.currentModeId) return;
    this.currentModeId = modeId;
    this.rebuildWallSketches();
    for (const listener of this.listeners) listener();
  }

  /** Rebuild every wall sketch's skin — they read the effective state at build time. */
  private rebuildWallSketches(): void {
    for (const sketch of this.viewer.scene?.wallSketches ?? []) {
      sketch.updateSkinningVisualization();
    }
    // Sketch objects re-resolve their frames per representation: wall objects
    // rest on the measured face in the Measured view, free objects get the
    // display-only anti-clip push out of the measured band.
    for (const sketchObject of this.viewer.scene?.sketchObjects ?? []) {
      sketchObject.rebuildGlyph();
    }
  }

  dispose(): void {
    this.attachedPersona?.removeEventListener('mode_changed', this.modeListener);
    this.attachedPersona = null;
    this.viewer.personaManager?.removeEventListener('persona_manager_stack_changed', this.personaListener);
    this.viewer.removeEventListener('scene_changed', this.sceneChangedListener);
    this.attachScene(null);
    this.listeners.clear();
  }
}
